import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { FormInput, FormTextarea, FormRoleSelector, FormDepartmentSelector, FormAvatarUpload } from '../../base';
import { useCreateUser, useUpdateUser } from '@/hooks/queries/useUsers';
import { z } from 'zod';
import { UserRole } from '@/services/apis/users';

const userFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.email('Invalid email address').refine((email) => email.endsWith('@viaphoton.com'), {
    message: 'Email must be from @viaphoton.com domain',
  }),
  position: z.string().min(1, 'Position is required'),
  positionDescription: z.string().optional(),
  role: z.enum(UserRole),
  departmentId: z.string().nullable().optional(),
  avatarFileId: z.uuid().nullable().optional(),
});

export type UserFormData = z.infer<typeof userFormSchema>;

interface UserFormProps {
  userId?: string | null;
  defaultValues?: Partial<UserFormData> & { avatarFile?: { id: string; url: string } | null };
  onCancel?: () => void;
  onSuccess?: () => void;
  isLoadingUser?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ userId, defaultValues, onCancel, onSuccess, isLoadingUser = false }) => {
  const { t } = useTranslation('admin');
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const isSubmitting = createUserMutation.isPending || updateUserMutation.isPending;

  const { control, handleSubmit, reset } = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      position: '',
      positionDescription: '',
      role: UserRole.LEARNER,
      departmentId: '',
      avatarFileId: null,
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (defaultValues && !isLoadingUser) {
      reset(defaultValues);
    }
  }, [defaultValues, isLoadingUser, reset]);

  const handleFormSubmit: SubmitHandler<UserFormData> = async (data) => {
    try {
      if (userId) {
        // For updates, pass null to clear departmentId, undefined means no change
        const updateData = {
          ...data,
          // Convert empty string to null to explicitly clear the department
          departmentId: data.departmentId === '' || data.departmentId === undefined ? null : data.departmentId,
        };
        await updateUserMutation.mutateAsync({ id: userId, data: updateData });
        addToast({
          title: t('users.actions.updateSuccess', { defaultValue: 'User updated successfully' }),
          color: 'success',
        });
      } else {
        // For creates, convert null/empty to undefined (CreateUserRequest doesn't accept null)
        const createData = {
          ...data,
          avatarFileId: data.avatarFileId ?? undefined,
          departmentId: data.departmentId || undefined,
        };
        await createUserMutation.mutateAsync(createData);
        addToast({
          title: t('users.actions.createSuccess', { defaultValue: 'User created successfully' }),
          color: 'success',
        });
      }
      // Only call onSuccess if the mutation was successful
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      addToast({
        title: error?.message || t('users.actions.error', { defaultValue: 'An error occurred' }),
        color: 'danger',
      });
      // Don't call onSuccess on error, so modal stays open
    }
  };

  return (
    <div className="space-y-4">
      <FormAvatarUpload
        name="avatarFileId"
        control={control}
        isDisabled={isSubmitting || isLoadingUser}
        avatarFile={defaultValues?.avatarFile || null}
      />
      <div className="grid grid-cols-2 gap-4">
        <FormInput
          name="firstName"
          control={control}
          label={t('users.form.firstName.label', { defaultValue: 'First Name' })}
          placeholder={t('users.form.firstName.placeholder', { defaultValue: 'Enter first name' })}
          isRequired
          isDisabled={isSubmitting || isLoadingUser}
        />
        <FormInput
          name="lastName"
          control={control}
          label={t('users.form.lastName.label', { defaultValue: 'Last Name' })}
          placeholder={t('users.form.lastName.placeholder', { defaultValue: 'Enter last name' })}
          isRequired
          isDisabled={isSubmitting || isLoadingUser}
        />
      </div>

      <FormInput
        name="email"
        control={control}
        label={t('users.form.email.label', { defaultValue: 'Email' })}
        placeholder={t('users.form.email.placeholder', { defaultValue: 'user@viaphoton.com' })}
        type="email"
        isRequired
        isDisabled={isSubmitting || isLoadingUser}
        description={t('users.form.email.description', { defaultValue: 'Must be from @viaphoton.com domain' })}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormInput
          name="position"
          control={control}
          label={t('users.form.position.label', { defaultValue: 'Position' })}
          placeholder={t('users.form.position.placeholder', { defaultValue: 'Enter position' })}
          isRequired
          isDisabled={isSubmitting || isLoadingUser}
        />
        <FormRoleSelector
          name="role"
          control={control}
          label={t('users.form.role.label', { defaultValue: 'Role' })}
          placeholder={t('users.form.role.placeholder', { defaultValue: 'Select role' })}
          isRequired
          isDisabled={isSubmitting || isLoadingUser}
        />
      </div>

      <FormTextarea
        name="positionDescription"
        control={control}
        label={t('users.form.positionDescription.label', { defaultValue: 'Position Description' })}
        placeholder={t('users.form.positionDescription.placeholder', {
          defaultValue: 'Enter position description (optional)',
        })}
        isDisabled={isSubmitting || isLoadingUser}
      />

      <FormDepartmentSelector
        name="departmentId"
        control={control}
        label={t('users.form.department.label', { defaultValue: 'Department' })}
        placeholder={t('users.form.department.placeholder', { defaultValue: 'Select a department' })}
        isDisabled={isSubmitting || isLoadingUser}
      />

      <div className="flex gap-2 justify-end pt-4">
        {onCancel && (
          <Button type="button" variant="light" onPress={onCancel} isDisabled={isSubmitting || isLoadingUser}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
        )}
        <Button
          type="button"
          color="primary"
          onPress={() => handleSubmit(handleFormSubmit)()}
          isLoading={isSubmitting || isLoadingUser}
          isDisabled={isSubmitting || isLoadingUser}
        >
          {t('common.save', { defaultValue: 'Save' })}
        </Button>
      </div>
    </div>
  );
};

export default UserForm;
